import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { deletePhoto } from '../api/photos';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PhotoView({ user }) {
  const { photoId } = useParams();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadPhoto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoId]);

  const loadPhoto = async () => {
    try {
      const response = await fetch(`${API_URL}/api/photos/${photoId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load photo');
      }

      const data = await response.json();
      setPhoto(data);
    } catch (error) {
      toast.error('Failed to load photo');
      navigate('/feed');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownload = async () => {
    if (!photo?.photo_url) return;
    
    try {
      const response = await fetch(photo.photo_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photo_${photoId}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Photo downloaded!');
    } catch (error) {
      toast.error('Failed to download photo');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deletePhoto(photoId);
      toast.success('Photo deleted successfully');
      navigate('/feed');
    } catch (error) {
      toast.error(error.message || 'Failed to delete photo');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="spinner" style={{ borderTopColor: 'white' }} />
      </div>
    );
  }

  if (!photo) {
    return null;
  }

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        background: 'black',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100
      }}
      data-testid="photo-view"
    >
      {/* Top bar with back button */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        padding: '1rem',
        background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10
      }}>
        <button
          onClick={handleBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            borderRadius: '9999px',
            color: 'white',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            backdropFilter: 'blur(8px)'
          }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleDownload}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '9999px',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              backdropFilter: 'blur(8px)'
            }}
            data-testid="download-button"
          >
            <Download size={18} />
          </button>

          {/* Delete button - only show for photo owner */}
          {photo.is_owner && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'rgba(239, 68, 68, 0.8)',
                border: 'none',
                borderRadius: '9999px',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                backdropFilter: 'blur(8px)'
              }}
              data-testid="delete-button"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20
          }}
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '1.5rem',
              maxWidth: '320px',
              margin: '1rem'
            }}
            onClick={e => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <h3 style={{ 
              fontSize: '1.125rem', 
              fontWeight: 600, 
              marginBottom: '0.5rem',
              color: '#1a1a1a'
            }}>
              Delete Photo?
            </h3>
            <p style={{ 
              fontSize: '0.875rem', 
              color: '#666', 
              marginBottom: '1.5rem' 
            }}>
              This will permanently delete the photo and all associated messages. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #ddd',
                  background: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: '#ef4444',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.7 : 1
                }}
                data-testid="confirm-delete-button"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full screen photo */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 0'
      }}>
        <img
          src={photo.photo_url}
          alt="Full size photo"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
          }}
          data-testid="full-photo"
        />
      </div>

      {/* Watermark - bottom left */}
      <div
        style={{
          position: 'absolute',
          bottom: '1.5rem',
          left: '1rem',
          zIndex: 10
        }}
        data-testid="photo-watermark"
      >
        {photo.watermark?.url && photo.watermark.url.length > 0 ? (
          <a
            href={photo.watermark.url.startsWith('http') ? photo.watermark.url : `https://${photo.watermark.url}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.875rem',
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 500,
              textDecoration: 'none'
            }}
          >
            <span>📷</span>
            {photo.watermark?.text || photo.owner_name}
            <ExternalLink size={14} style={{ opacity: 0.7 }} />
          </a>
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.875rem',
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            <span>📷</span>
            {photo.watermark?.text || photo.owner_name}
          </span>
        )}
      </div>

      {/* Photo info - bottom right */}
      <div
        style={{
          position: 'absolute',
          bottom: '1.5rem',
          right: '1rem',
          zIndex: 10,
          color: 'rgba(255,255,255,0.7)',
          fontSize: '0.75rem',
          textAlign: 'right'
        }}
      >
        <div>{new Date(photo.created_at).toLocaleDateString()}</div>
        <div>{new Date(photo.created_at).toLocaleTimeString()}</div>
      </div>
    </div>
  );
}

export default PhotoView;
